import {
    Button,
    DatePicker,
    Input,
    InputNumber,
    Radio,
    Select,
    Space,
    Switch,
    message,
} from "antd";
import {useNavigate, useParams} from "react-router-dom";
import React, {useEffect, useMemo, useState} from "react";
import dayjs from "dayjs";
import {
    ArrowLeft,
    BadgeInfo,
    Boxes,
    Building2,
    CalendarDays,
    History,
    MapPin,
    Phone,
    RotateCcw,
    Save,
    Send,
    Smartphone,
    UserRound,
} from 'lucide-react';
import {APIv1} from "../../api";
import {images} from "../../constants";
import {useTranslation} from "react-i18next";
import {checkIsPhoneCorrect} from "../../utils";
import {canManageOrderHistoryRange} from "../../utils/userPermissions";
import ThemeS2P from "../../components/UserProfileCom/Scan2Pay/ThemeS2P/ThemeS2P";
import ShowUserPicture from "../../components/UserProfileCom/ShowUserPicture/ShowUserPicture";
import {
    ORDER_HISTORY_RANGE_MAX,
    ORDER_HISTORY_RANGE_MIN,
    normalizeOrderHistoryRange,
} from "../../components/UserProfileCom/OrderHistoryRange/OrderHistoryRange";
import TelegramGroupSection, {
    createEmptyTelegramGroup,
    mapTelegramGroupsToFormData,
    mapTelegramGroupsToPayload,
} from "./Sections/TelegramGroupSection";
import './AddNewCompanyForm.scss'
import {playFeedbackWave} from "../../utils/feedbackWave";

const {RangePicker} = DatePicker;

const companyStatusOptions = (t) => [
    {label: t("common.active"), value: "ACTIVE"},
    {label: t("common.disconnected"), value: "DISCONNECTED"},
    {label: t("common.inactive"), value: "INACTIVE"},
    {label: t("common.controlOff"), value: "CONTROL_OFF"},
];

const formatApiValidationMessage = (errorMessage) => {
    if (!errorMessage) return "";
    if (typeof errorMessage === "string") return errorMessage;
    if (Array.isArray(errorMessage)) {
        return errorMessage
            .map((item, index) => {
                const formattedItem = formatApiValidationMessage(item);
                return formattedItem ? `${index + 1}. ${formattedItem}` : "";
            })
            .filter(Boolean)
            .join("; ");
    }
    if (typeof errorMessage === "object") {
        return Object.entries(errorMessage)
            .map(([field, value]) => {
                if (Array.isArray(value) && value.every(item => typeof item === "object" && !Array.isArray(item))) {
                    return value
                        .map((item, index) => {
                            const formattedItem = formatApiValidationMessage(item);
                            return formattedItem ? `${field}[${index + 1}]: ${formattedItem}` : "";
                        })
                        .filter(Boolean)
                        .join("; ");
                }

                const formattedValue = formatApiValidationMessage(value);
                return formattedValue ? `${field}: ${formattedValue}` : "";
            })
            .filter(Boolean)
            .join("; ");
    }

    return String(errorMessage);
};

const initialFormData = {
    start_date: null,
    end_date: null,
    name: "",
    public_name: "",
    address: "",
    inn: "",
    version: "",
    phone: "",
    user: [],
    company_software: [],
    company_type: "",
    status: "ACTIVE",
    is_scan2pay_tip: false,
    scan2pay_theme: 'cream',
    scan2pay_default_tip_percent: 0,
    order_history_range: null,
    telegram_groups: [createEmptyTelegramGroup()],
    deleted_telegram_groups: [],
    permissions: {
        sent_sms: false,
        perm_click: false,
        perm_payme: false,
        perm_uzum: false,
        perm_anor: false,
    },
};

const s2pPercentageOptions = [
    {label: '😕 0%', value: 0},
    {label: '🙂 5%', value: 5},
    {label: '☺️ 10%', value: 10},
    {label: '😍 15%', value: 15},
    {label: '🤯️ 20%', value: 20},
];

const normalizeUzbekPhone = value => {
    const digits = String(value || "").replace(/\D/g, "");

    if (digits.length > 9 && digits.startsWith("998")) {
        return digits.substring(3, 12);
    }

    return digits.substring(0, 9);
};

const formatUzbekPhoneForDisplay = value => {
    const localPhone = normalizeUzbekPhone(value);
    const parts = [
        localPhone.substring(0, 2),
        localPhone.substring(2, 5),
        localPhone.substring(5, 7),
        localPhone.substring(7, 9),
    ].filter(Boolean);

    return parts.join(" ");
};

const formatUzbekPhoneForApi = value => {
    const localPhone = normalizeUzbekPhone(value);

    return localPhone ? `+998${localPhone}` : "";
};

const isValidUzbekPhone = value => {
    const localPhone = normalizeUzbekPhone(value);

    return localPhone.length === 0 || localPhone.length === 9;
};


const Field = ({label, required, children, wide, icon}) => (
    <div className={`create-device__field ${wide ? "create-device__field--wide" : ""}`}>
        <div className="create-device__field-label">
            {icon && <span className="create-device__field-icon">{icon}</span>}
            <span className={required ? "create-device__required-label" : ""}>{label}</span>
        </div>
        <div className="create-device__field-control">
            {children}
        </div>
    </div>
);

const Section = ({index, title, children}) => (
    <section className="create-device__forms-container">
        <div className="create-device__section-header">
            <span className="create-device__section-index">{index}</span>
            <h2 className="create-device__forms-title">{title}</h2>
        </div>
        {children}
    </section>
);

const permissionFields = [
    {key: 'sent_sms', icon: <Send size={16}/>, labelKey: 'row5'},
    {key: 'perm_payme', logo: images.paymeLogo, labelKey: 'row8'},
    {key: 'perm_click', logo: images.clickLogo, labelKey: 'row9'},
    {key: 'perm_uzum', logo: images.uzumLogo, labelKey: 'row10'},
    {key: 'perm_anor', logo: images.anorLogo, labelKey: 'row11'},
];

const mapCompanyResponseToFormData = (data, fallback = initialFormData) => ({
    start_date: data.start_date ? dayjs(data.start_date) : null,
    end_date: data.end_date ? dayjs(data.end_date) : null,
    name: data.name || "",
    public_name: data.public_name || "",
    address: data.address || "",
    inn: data.inn || fallback.inn || "",
    version: data.versions || data.version || fallback.version || "",
    phone: data.phone_number === undefined || data.phone_number === null
        ? fallback.phone || ""
        : checkIsPhoneCorrect(data.phone_number),
    company_software: data.software?.map(software => software.name) || data.company_software || fallback.company_software || [],
    company_type: data.company_type?.name || data.company_type || fallback.company_type || "",
    status: data.status || fallback.status || "ACTIVE",
    user: data.user?.username ? [data.user.username] : fallback.user || [],
    is_scan2pay_tip: data.is_scan2pay_tip ?? fallback.is_scan2pay_tip,
    scan2pay_theme: data.scan2pay_theme || fallback.scan2pay_theme,
    scan2pay_default_tip_percent: data.scan2pay_default_tip_percent ?? fallback.scan2pay_default_tip_percent,
    order_history_range: 'order_history_range' in data
        ? normalizeOrderHistoryRange(data.order_history_range)
        : fallback.order_history_range,
    telegram_groups: mapTelegramGroupsToFormData(data.telegram_groups, fallback.telegram_groups),
    deleted_telegram_groups: [],
    permissions: {
        sent_sms: Boolean(data.send_sms),
        perm_click: Boolean(data.click),
        perm_payme: Boolean(data.pay_me),
        perm_uzum: Boolean(data.uzum ?? data.apelsin),
        perm_anor: Boolean(data.anor),
    },
});

const AddNewCompanyForm = () => {
    const {id} = useParams();
    const {t} = useTranslation();
    const navigate = useNavigate();
    const [formData, setFormData] = useState(initialFormData);
    const [companyImages, setCompanyImages] = useState({});
    const [companyVersions, setCompanyVersions] = useState([]);
    const [companySoftware, setCompanySoftware] = useState([]);
    const [companyTypes, setCompanyTypes] = useState([]);
    const [savedCompanyData, setSavedCompanyData] = useState(null);
    const [userOptionsData, setUserOptionsData] = useState([]);
    const [userData, setUserData] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);

    const isEditing = Boolean(id);
    const canEditOrderHistoryRange = canManageOrderHistoryRange(userData?.data);
    const footerText = isEditing && formData.inn
        ? t("pages.companies.createColumns.footerEdit", {inn: formData.inn})
        : t("pages.companies.createColumns.footerCreate");

    const companyImageData = useMemo(() => ({
        billingLogo: companyImages.billingLogo,
        logo: companyImages.logo,
        banner: companyImages.banner,
    }), [companyImages]);

    useEffect(() => {
        const items = JSON.parse(localStorage.getItem('user'));
        if (items?.token !== userData.token) {
            setUserData(items || {});
        }
    }, [userData.token]);

    useEffect(() => {
        const getUsersData = async () => {
            try {
                const response = await APIv1.get('/user/list');
                setUserOptionsData(response.data.map(user => ({
                    label: user.username,
                    value: user.username,
                    desc: user.username,
                    emoji: '👤',
                })));
            } catch (err) {
                console.error('Something went wrong:', err);
            }
        };

        getUsersData();
    }, []);

    useEffect(() => {
        if (!id || !userData?.token) return;

        const fetchCompanyDetail = async () => {
            try {
                const response = await APIv1.get(
                    `/company/${id}`,
                    {headers: {Authorization: `Token ${userData.token}`}}
                );
                const mappedFormData = mapCompanyResponseToFormData(response.data);

                setSavedCompanyData(response.data);
                setFormData(mappedFormData);

                const imagesResponse = await APIv1.get(
                    `/company/profile/images/contents/${response.data.inn}`,
                    {headers: {Authorization: `Token ${userData.token}`}}
                );

                setCompanyImages({
                    billingLogo: imagesResponse.data.data.billing_logo,
                    logo: imagesResponse.data.data.logo,
                    banner: imagesResponse.data.data.banner
                });
            } catch (err) {
                console.error('Something went wrong:', err);
            }
        };

        fetchCompanyDetail();
    }, [id, userData?.token]);

    useEffect(() => {
        if (!userData.token) return;

        const fetchVersions = async () => {
            try {
                const versionsResponse = await APIv1.get('/version/list/', {
                    headers: {
                        Authorization: `Token ${userData.token}`
                    },
                });
                setCompanyVersions(versionsResponse.data.map(item => ({
                    value: item.version_number,
                    label: item.version_number,
                })))
            } catch (err) {
                console.error('Something went wrong with versions:', err);
            }
        }

        fetchVersions();
    }, [userData?.token])

    useEffect(() => {
        if (!userData.token) return;

        const fetchSoftware = async () => {
            try {
                const softwareResponse = await APIv1.get('/software/list/', {
                    headers: {
                        Authorization: `Token ${userData.token}`
                    },
                });
                setCompanySoftware(softwareResponse.data.results.map(item => ({
                    value: item.name,
                    label: item.name,
                })))
            } catch (err) {
                console.error('Something went wrong with software:', err);
            }
        }

        fetchSoftware();
    }, [userData?.token])

    useEffect(() => {
        if (!userData.token) return;

        const fetchCompanyTypes = async () => {
            try {
                const companyTypeResponse = await APIv1.get('/company/type/', {
                    headers: {
                        Authorization: `Token ${userData.token}`
                    },
                });
                setCompanyTypes(companyTypeResponse.data.map(item => ({
                    value: item.name,
                    label: item.name,
                })))
            } catch (err) {
                console.error('Something went wrong with company types:', err);
            }
        }

        fetchCompanyTypes();
    }, [userData?.token])

    const updateField = (name, value) => {
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const updatePermission = (name, checked) => {
        setFormData(prev => ({
            ...prev,
            permissions: {
                ...prev.permissions,
                [name]: checked
            }
        }));
    };

    const handleInputChange = (e) => {
        const {name, value, type, checked} = e.target;
        updateField(
            name,
            name === 'phone'
                ? normalizeUzbekPhone(value)
                : type === 'checkbox'
                    ? checked
                    : value
        );
    };

    const handleDateChange = (dates) => {
        setFormData(prev => ({
            ...prev,
            start_date: dates?.[0] || null,
            end_date: dates?.[1] || null
        }));
    };

    const handleSubmit = async (token) => {
        try {
            if (!token || typeof token !== 'string' || token.trim() === '') {
                message.error(t("pages.companies.createColumns.messages.invalidToken"));
                return;
            }

            if (!isValidUzbekPhone(formData.phone)) {
                message.error(t("pages.companies.createColumns.messages.invalidPhone"));
                return;
            }

            setIsSubmitting(true);

            const user = Array.isArray(formData.user) ? formData.user[0] : formData.user;

            const body = {
                name: formData.name,
                public_name: formData.public_name,
                inn: formData.inn,
                version: formData.version,
                phone_number: formatUzbekPhoneForApi(formData.phone),
                address: formData.address,
                user,
                company_software: formData.company_software || [],
                company_type: formData.company_type,
                status: formData.status,
                is_scan2pay_tip: formData.is_scan2pay_tip,
                scan2pay_theme: formData.scan2pay_theme,
                scan2pay_default_tip_percent: formData.scan2pay_default_tip_percent,
                send_sms: formData.permissions?.sent_sms,
                start_date: formData.start_date,
                end_date: formData.end_date,
                pay_me: formData.permissions?.perm_payme,
                click: formData.permissions?.perm_click,
                uzum: formData.permissions?.perm_uzum,
                anor: formData.permissions?.perm_anor,
                telegram_group: mapTelegramGroupsToPayload(
                    formData.telegram_groups,
                    formData.deleted_telegram_groups
                ),
                order_history_range: canEditOrderHistoryRange
                    ? normalizeOrderHistoryRange(formData?.order_history_range)
                    : undefined,
            };

            const response = await APIv1.post('/company/create/', body, {
                headers: {
                    Authorization: `Token ${token.trim()}`,
                    'Content-Type': 'application/json',
                },
            });

            if (response.status === 201 || response.status === 200) {
                playFeedbackWave('success');
                message.success(t("pages.companies.createColumns.messages.success1"));
                const responseData = response.data.data;
                const updatedFormData = mapCompanyResponseToFormData(responseData, formData);

                setSavedCompanyData(responseData);
                setFormData(updatedFormData);
            } else {
                message.info(t("pages.companies.createColumns.messages.info1"));
            }
        } catch (err) {
            console.error('Error submitting company data:', err);
            const errorMessage = formatApiValidationMessage(err.response?.data?.message)
                || t("pages.companies.createColumns.messages.error1");
            playFeedbackWave('failure');
            message.error(errorMessage);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleClear = () => {
        setFormData(savedCompanyData ? mapCompanyResponseToFormData(savedCompanyData, formData) : initialFormData);
        message.info(t("pages.companies.createColumns.messages.info1"));
    };

    return (
        <div className='content_container create-device create-company'>
            <div className='create-device__header'>
                <div className='create-device__heading'>
                    <span className='create-device__eyebrow'>
                        {isEditing
                            ? t("pages.companies.createColumns.modeEdit")
                            : t("pages.companies.createColumns.modeCreate")}
                    </span>
                    <h1>{t("pages.companies.createColumns.title")}</h1>
                    {formData.inn && (
                        <span className='create-device__serial'>#{formData.inn}</span>
                    )}
                </div>
                <Button
                    className='create-device__action-button create-device__action-button--secondary'
                    icon={<ArrowLeft size={16}/>}
                    onClick={() => navigate(-1)}
                >
                    {t("pages.companies.createColumns.button1")}
                </Button>
            </div>

            <div className='create-device__forms'>
                <Section index="01" title={t("pages.companies.createColumns.detailsSectionTitle")}>
                    <div className="create-device__field-grid">
                        <Field
                            label={t("pages.companies.createColumns.row1")}
                            icon={<CalendarDays size={16}/>}
                            required
                        >
                            <RangePicker
                                className="create-device__control"
                                showTime={{format: 'HH:mm'}}
                                format='YYYY-MM-DD HH:mm:ss'
                                onChange={handleDateChange}
                                value={[formData.start_date, formData.end_date]}
                                placeholder={[
                                    t("pages.companies.createColumns.placeholder1"),
                                    t("pages.companies.createColumns.placeholder2")
                                ]}
                            />
                        </Field>

                        <Field label={t("pages.companies.createColumns.row2")} required icon={<Building2 size={16}/>}>
                            <Input
                                className="create-device__control"
                                placeholder={t("pages.companies.createColumns.placeholder3")}
                                name="name"
                                value={formData.name}
                                onChange={handleInputChange}
                                allowClear
                            />
                        </Field>

                        <Field label={t("pages.companies.createColumns.row16")} icon={<BadgeInfo size={16}/>}>
                            <Input
                                className="create-device__control"
                                placeholder={t("pages.companies.createColumns.placeholder7")}
                                name="public_name"
                                value={formData.public_name}
                                onChange={handleInputChange}
                                allowClear
                            />
                        </Field>

                        <Field label={t("pages.companies.createColumns.row4")} required icon={<BadgeInfo size={16}/>}>
                            <Input
                                className="create-device__control"
                                placeholder={t("pages.companies.createColumns.placeholder5")}
                                name="inn"
                                value={formData.inn}
                                onChange={handleInputChange}
                                allowClear
                            />
                        </Field>

                        <Field label={t("pages.companies.createColumns.row6")} icon={<Phone size={16}/>}>
                            <Input.Group compact className="create-company__phone">
                                <Input
                                    className="create-company__phone-prefix"
                                    value="+998"
                                    disabled
                                />
                                <Input
                                    className="create-company__phone-number"
                                    type="text"
                                    name="phone"
                                    inputMode="numeric"
                                    maxLength={12}
                                    value={formatUzbekPhoneForDisplay(formData.phone)}
                                    onChange={handleInputChange}
                                    allowClear
                                />
                            </Input.Group>
                        </Field>

                        <Field
                            label={t("pages.companies.createColumns.row3")}
                            icon={<MapPin size={16}/>}
                        >
                            <Input
                                className="create-device__control"
                                placeholder={t("pages.companies.createColumns.placeholder4")}
                                name="address"
                                value={formData.address}
                                onChange={handleInputChange}
                                allowClear
                            />
                        </Field>

                        <Field
                            label={t("common.status")}
                            required
                            icon={<BadgeInfo size={16}/>}
                        >
                            <Select
                                className="create-device__control"
                                value={formData.status}
                                onChange={(value) => updateField("status", value)}
                                options={companyStatusOptions(t)}
                                virtual={false}
                            />
                        </Field>
                    </div>

                </Section>

                <Section index="02" title={t("pages.companies.createColumns.ownershipSectionTitle")}>
                    <div className="create-device__field-grid">
                        <Field label={t("pages.companies.createColumns.row7")} required icon={<UserRound size={16}/>}>
                            <Select
                                className="create-device__control"
                                mode='tags'
                                placeholder={t("pages.companies.createColumns.placeholder6")}
                                value={formData.user}
                                onChange={(value) => updateField("user", value)}
                                optionLabelProp='label'
                                options={userOptionsData}
                                virtual={false}
                                optionRender={option => (
                                    <Space>
                                        <span role='img' aria-label={option.data.label}>
                                            {option.data.emoji}
                                        </span>
                                        {option.data.desc}
                                    </Space>
                                )}
                                allowClear
                            />
                        </Field>

                        <Field label={t("pages.companies.createColumns.row12")} icon={<Smartphone size={16}/>}>
                            <Select
                                className="create-device__control"
                                value={formData.version || null}
                                onChange={(value) => updateField("version", value)}
                                options={companyVersions}
                                virtual={false}
                                allowClear
                            />
                        </Field>

                        <Field label={t("pages.companies.createColumns.row18")} icon={<Boxes size={16}/>}>
                            <Select
                                className="create-device__control"
                                mode="multiple"
                                placeholder={t("pages.companies.createColumns.placeholder8")}
                                value={formData.company_software}
                                onChange={(value) => updateField("company_software", value)}
                                optionLabelProp='label'
                                options={companySoftware}
                                virtual={false}
                                allowClear
                            />
                        </Field>

                        <Field label={t("pages.companies.createColumns.row17")} required icon={<Building2 size={16}/>}>
                            <Select
                                className="create-device__control"
                                placeholder={t("pages.companies.createColumns.placeholder9")}
                                value={formData.company_type || undefined}
                                onChange={(value) => updateField("company_type", value)}
                                optionLabelProp='label'
                                options={companyTypes}
                                virtual={false}
                            />
                        </Field>
                    </div>
                </Section>

                <Section index="03" title={t("pages.companies.createColumns.permissionsSectionTitle")}>
                    <div className="create-company__permission-grid">
                        {permissionFields.map(({key, icon, logo, labelKey}) => (
                            <label className="create-company__permission-card" key={key}>
                                <span className="create-company__permission-label">
                                    {logo ? (
                                        <img
                                            src={logo}
                                            alt=""
                                            className="create-company__permission-logo"
                                        />
                                    ) : (
                                        <span className="create-company__permission-icon">{icon}</span>
                                    )}
                                    {t(`pages.companies.createColumns.${labelKey}`)}
                                </span>
                                <Switch
                                    checked={formData.permissions[key]}
                                    onChange={(checked) => updatePermission(key, checked)}
                                />
                            </label>
                        ))}
                        {canEditOrderHistoryRange && (
                            <div className="create-company__permission-card">
                                <span className="create-company__permission-label">
                                    <span className="create-company__permission-icon"><History size={16}/></span>
                                    {t("pages.companies.createColumns.row19")}
                                </span>
                                <InputNumber
                                    className="create-company__permission-number"
                                    size="small"
                                    min={ORDER_HISTORY_RANGE_MIN}
                                    max={ORDER_HISTORY_RANGE_MAX}
                                    step={1}
                                    precision={0}
                                    controls={false}
                                    placeholder={t("pages.user.profile.orderHistoryRange.allShort")}
                                    value={formData.order_history_range}
                                    onChange={(value) => updateField("order_history_range", normalizeOrderHistoryRange(value))}
                                    suffix={t("pages.user.profile.orderHistoryRange.unit")}
                                />
                            </div>
                        )}
                    </div>

                    <TelegramGroupSection
                        telegramGroups={formData.telegram_groups}
                        onChange={setFormData}
                    />
                </Section>

                <div className="create-company__paired-sections">
                    <Section index="04" title={t("pages.companies.createColumns.scan2paySectionTitle")}>
                        <div className="create-company__scan2pay-stack">
                            <Field label={t("pages.companies.createColumns.row13")}>
                                <div className="create-company__switch-row">
                                    <Switch
                                        checked={formData.is_scan2pay_tip}
                                        onChange={(checked) => updateField("is_scan2pay_tip", checked)}
                                    />
                                </div>
                            </Field>

                            <Field label={t("pages.companies.createColumns.row15")}>
                                <Radio.Group
                                    className="create-company__tip-options"
                                    name="scan2pay_default_tip_percent"
                                    options={s2pPercentageOptions}
                                    optionType="button"
                                    buttonStyle="solid"
                                    value={formData.scan2pay_default_tip_percent}
                                    onChange={handleInputChange}
                                />
                            </Field>

                            <Field label={t("pages.companies.createColumns.row14")}>
                                <div className="create-company__theme-picker">
                                    <ThemeS2P
                                        t={t}
                                        currentTheme={formData.scan2pay_theme || "light"}
                                        onThemeChange={(key) => updateField('scan2pay_theme', key)}
                                    />
                                </div>
                            </Field>
                        </div>
                    </Section>

                    <Section index="05" title={t("pages.companies.createColumns.imagesSectionTitle")}>
                        <div className="create-company__media-panel">
                            <ShowUserPicture
                                data={companyImageData}
                                inn={formData.inn}
                                userToken={userData.token}
                                isEditing={true}
                                uploadPlacement="card"
                            />
                        </div>
                    </Section>
                </div>
            </div>

            <div className='create-device__footer'>
                <span className='create-device__footer-copy'>
                    {footerText}
                </span>
                <div className='create-device__footer-buttons'>
                    <Button
                        className='create-device__action-button create-device__action-button--primary'
                        icon={<Save size={16}/>}
                        type='primary'
                        size='large'
                        onClick={() => handleSubmit(userData?.token)}
                        loading={isSubmitting}
                        disabled={!userData?.token}
                    >
                        {t("pages.companies.createColumns.button2")}
                    </Button>
                    <Button
                        type='default'
                        className='create-device__action-button create-device__action-button--secondary'
                        icon={<RotateCcw size={16}/>}
                        size='large'
                        onClick={handleClear}
                    >
                        {t("pages.companies.createColumns.button3")}
                    </Button>
                </div>
            </div>
        </div>
    )
}

export default AddNewCompanyForm;
